import { useEffect, useRef } from 'react';

/**
 * Scroll-reveal via IntersectionObserver. Returns a ref to attach to a container;
 * any descendant carrying the `.sg-reveal` class gets `.sg-in` added the moment it
 * scrolls into view, triggering the fade-up transition defined in index.css.
 *
 * Why a container-level observer rather than one-per-element: the marketing pages
 * have dozens of revealable blocks; a single observer with all targets is far
 * cheaper than dozens of hook instances. Elements are observed once and unobserved
 * after revealing (one-shot — no re-hide on scroll-out, which would feel janky).
 *
 * Respects prefers-reduced-motion: in that mode index.css already renders
 * `.sg-reveal` fully visible, and we skip wiring the observer entirely.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    if (
      typeof window === 'undefined' ||
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      // No observer: make sure nothing stays stuck hidden.
      root.querySelectorAll<HTMLElement>('.sg-reveal').forEach((el) => el.classList.add('sg-in'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('sg-in');
            observer.unobserve(entry.target); // one-shot
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );

    const targets = root.querySelectorAll<HTMLElement>('.sg-reveal');
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return ref;
}
